import React, { useEffect, useState } from 'react';
import { Card, Form, Table, Button, Row, Col, Modal, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../services/api';
import SearchSelect from './SearchSelect';
import { todayDR } from '../utils/dates';

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Diario' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' },
];

const emptyItem = () => ({
  product: '', description: '', quantity: 1, rate: 0, itbisRate: 0.18,
});

const OPEN_STATUS_LABEL = { draft: 'Borrador', contract_generated: 'Contrato generado', active: 'Activo' };

const todayStr = todayDR; // today on the Dominican calendar (not UTC's, which is tomorrow after 8 p.m.)

const RentalBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [clientId, setClientId] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [openRentals, setOpenRentals] = useState(null); // the client's open rentals, when the server asks to confirm a new one
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/clients').then(res => setClients(res.data)).catch(err => console.error(err));
    API.get('/products').then(res => setProducts(res.data)).catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (!id) return;
    API.get(`/rentals/${id}`).then(({ data }) => {
      setClientId(data.client?._id || '');
      setBillingPeriod(data.billingPeriod);
      setStartDate(data.startDate ? data.startDate.slice(0, 10) : todayStr());
      setEndDate(data.endDate ? data.endDate.slice(0, 10) : '');
      setNotes(data.notes || '');
      setItems(data.items?.length ? data.items.map(it => ({
        product: it.product || '',
        sku: it.sku,
        description: it.description,
        quantity: it.quantity,
        rate: it.rate,
        itbisRate: it.itbisRate,
        itbisExento: it.itbisExento,
        unidadMedida: it.unidadMedida,
      })) : [emptyItem()]);
    }).catch(err => {
      console.error('Error loading rental:', err);
      setError('No se pudo cargar el alquiler.');
    });
  }, [id]);

  const selectedClient = clients.find(c => c._id === clientId);
  const rentableProducts = products.filter(p => p.isRentable);

  const updateItem = (index, patch) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, ...patch } : it));
  };

  const handleProductChange = (index, productId) => {
    const product = products.find(p => p._id === productId);
    if (!product) {
      updateItem(index, { product: '' });
      return;
    }
    const rate = product.rentalRates?.[billingPeriod] ?? 0;
    updateItem(index, {
      product: productId,
      sku: product.sku,
      description: product.name,
      rate,
      itbisRate: product.itbisRate,
      itbisExento: product.itbisExento,
      unidadMedida: product.unidadMedida,
    });
  };

  // Changing the billing period re-suggests each line's rate from the newly
  // selected tier (still editable after) rather than leaving a stale rate
  // from the previously selected period.
  const handlePeriodChange = (newPeriod) => {
    setBillingPeriod(newPeriod);
    setItems(prev => prev.map(it => {
      if (!it.product) return it;
      const product = products.find(p => p._id === it.product);
      const rate = product?.rentalRates?.[newPeriod];
      return rate != null ? { ...it, rate } : it;
    }));
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

  const totals = items.reduce((acc, item) => {
    const lineTotal = (Number(item.quantity) || 0) * (Number(item.rate) || 0);
    acc.subtotal += lineTotal;
    if (!item.itbisExento) acc.itbis += lineTotal * (item.itbisRate ?? 0.18);
    return acc;
  }, { subtotal: 0, itbis: 0 });
  const grandTotal = totals.subtotal + totals.itbis;

  const handleSubmit = async (confirmOpenRentals = false) => {
    setError('');
    setOpenRentals(null);

    if (!clientId) {
      setError('Selecciona un cliente.');
      return;
    }
    if (!startDate) {
      setError('Selecciona una fecha de inicio.');
      return;
    }
    const validItems = items.filter(it => it.description && Number(it.quantity) > 0);
    if (validItems.length === 0) {
      setError('Agrega al menos una línea de equipo con descripción y cantidad.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        client: clientId,
        billingPeriod,
        startDate,
        endDate: endDate || null,
        notes,
        items: validItems.map(it => ({
          product: it.product || undefined,
          sku: it.sku,
          description: it.description,
          quantity: Number(it.quantity),
          rate: Number(it.rate),
          itbisExento: !!it.itbisExento,
          itbisRate: it.itbisRate,
          unidadMedida: it.unidadMedida,
        })),
      };
      if (id) {
        await API.put(`/rentals/${id}`, payload);
      } else {
        await API.post('/rentals', { ...payload, confirmOpenRentals });
      }
      navigate('/rentals');
    } catch (err) {
      // The client already has open rentals: ask before making another (a duplicate is the usual mistake).
      if (err.response?.data?.code === 'OPEN_RENTALS') { setOpenRentals(err.response.data.rentals); return; }
      setError(err.response?.data?.message || 'Error al guardar el alquiler.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid p-4">
      <h2 className="app-title mb-4" style={{ textAlign: 'left' }}>{id ? 'Editar Alquiler' : 'Nuevo Alquiler'}</h2>

      {error && <div className="alert alert-danger">{error}</div>}

      <Card className="shadow-sm p-3 mb-3">
        <Row>
          <Col md={5}>
            <Form.Label>Cliente</Form.Label>
            <SearchSelect
              options={clients}
              value={clientId}
              getKey={c => c._id}
              getLabel={c => `${c.name}${c.rnc ? ' — RNC ' + c.rnc : ''}`}
              onSelect={c => setClientId(c._id)}
              placeholder="Buscar cliente..."
              emptyLabel="Sin clientes coincidentes"
            />
          </Col>
          <Col md={3}>
            <Form.Label>Período de Facturación</Form.Label>
            <Form.Select value={billingPeriod} onChange={e => handlePeriodChange(e.target.value)}>
              {PERIOD_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label>Inicio</Form.Label>
            <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </Col>
          <Col md={2}>
            <Form.Label>Fin (opcional)</Form.Label>
            <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </Col>
        </Row>
        {selectedClient && (
          <div className="text-muted small mt-2">
            {selectedClient.address || 'Sin dirección registrada'} · {selectedClient.email || 'Sin email'} · {selectedClient.phone || 'Sin teléfono'}
          </div>
        )}
      </Card>

      <Card className="shadow-sm p-3 mb-3">
        <Card.Title>Equipo en Alquiler</Card.Title>
        <Table responsive className="mb-2">
          <thead>
            <tr>
              <th style={{ width: 50 }}>No.</th>
              <th style={{ minWidth: 180 }}>Equipo</th>
              <th style={{ minWidth: 180 }}>Descripción</th>
              <th style={{ width: 90 }}>Cantidad</th>
              <th style={{ width: 130 }}>Tarifa</th>
              <th style={{ width: 120 }} className="text-end">Total/Ciclo</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const lineTotal = (Number(item.quantity) || 0) * (Number(item.rate) || 0);
              return (
                <tr key={i}>
                  <td className="align-middle text-muted">{i + 1}</td>
                  <td>
                    <SearchSelect
                      options={rentableProducts}
                      value={item.product}
                      getKey={p => p._id}
                      getLabel={p => p.name}
                      onSelect={p => handleProductChange(i, p._id)}
                      placeholder="Buscar equipo..."
                      emptyLabel="Sin equipo alquilable coincidente"
                    />
                  </td>
                  <td>
                    <Form.Control
                      value={item.description}
                      onChange={e => updateItem(i, { description: e.target.value })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number" min="0" step="1"
                      value={item.quantity}
                      onChange={e => updateItem(i, { quantity: e.target.value })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number" min="0" step="0.01"
                      value={item.rate}
                      onChange={e => updateItem(i, { rate: e.target.value })}
                    />
                  </td>
                  <td className="text-end align-middle">{money(lineTotal)}</td>
                  <td className="align-middle">
                    <Button size="sm" variant="outline-danger" onClick={() => removeItem(i)} disabled={items.length === 1}>
                      <i className="bi bi-x" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Button size="sm" variant="outline-secondary" onClick={addItem}>+ Agregar Línea</Button>

        <Form.Group className="mt-3">
          <Form.Label>Notas</Form.Label>
          <Form.Control as="textarea" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
        </Form.Group>

        <div className="d-flex justify-content-end mt-4">
          <div style={{ minWidth: 260 }}>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted">Subtotal</span>
              <span>{money(totals.subtotal)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">ITBIS</span>
              <span>{money(totals.itbis)}</span>
            </div>
            <hr style={{ borderColor: 'var(--mm-line)' }} />
            <div className="d-flex justify-content-between">
              <span style={{ fontWeight: 700 }}>Total por Ciclo</span>
              <span style={{ fontWeight: 700, fontSize: '1.3rem', color: 'var(--mm-cyan)' }}>{money(grandTotal)}</span>
            </div>
          </div>
        </div>
      </Card>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="outline-secondary" onClick={() => navigate('/rentals')}>Cancelar</Button>
        <Button onClick={() => handleSubmit()} disabled={saving}>
          {saving ? 'Guardando...' : (id ? 'Guardar Cambios' : 'Guardar Alquiler')}
        </Button>
      </div>

      {openRentals && (
        <Modal show centered onHide={() => setOpenRentals(null)}>
          <Modal.Header closeButton><Modal.Title>Este cliente ya tiene {openRentals.length === 1 ? 'un alquiler abierto' : `${openRentals.length} alquileres abiertos`}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Table size="sm" className="mb-2">
              <tbody>
                {openRentals.map(r => (
                  <tr key={r._id}>
                    <td className="text-nowrap"><strong>{r.number}</strong></td>
                    <td><Badge bg={r.status === 'active' ? 'success' : 'secondary'}>{OPEN_STATUS_LABEL[r.status] || r.status}</Badge></td>
                    <td>
                      {r.equipment || '—'}
                      {r.sameEquipment && <div className="small" style={{ color: 'var(--mm-red)' }}>Mismo equipo que este alquiler</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="small text-muted">
              Si es el mismo servicio, renueva o edita el alquiler que ya existe en lugar de crear otro. Crea uno nuevo solo si de verdad es
              otro alquiler para este cliente.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setOpenRentals(null)}>Volver</Button>
            <Button variant="danger" disabled={saving} onClick={() => handleSubmit(true)}>Crear de todas formas</Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default RentalBuilder;
